// Roland S-770 / S-760 / S-750 Floppy Disk Image Exporter
//
// Creates a 1.44 MB floppy disk image (.img) with 16-bit LE signed PCM
// samples in the Roland S-7x0 disk format.
//
// Disk layout (1.44 MB = 1,474,560 bytes, 2880 blocks × 512 bytes):
//   0x00000 - 0x003FF  Volume header (tag at 0x04, volume name at 0x100)
//   0x00400 - 0x01BFF  FAT (16-bit LE entries, 2880 entries × 2 bytes = 5760 B)
//   0x01C00 - 0x03BFF  Patch directory (16 patches × 512 bytes)
//   0x03C00 - 0x05FFF  PCH sample directory (up to 512 entries × 32 bytes, padded)
//   0x06000 - end      Audio data (16-bit LE signed PCM, 8192-byte aligned units)
//
// PCH sample directory entry (32 bytes):
//   0x00-0x0F: 16-byte name (XYZ:Name...., ':' at byte 3, space-padded)
//              byte 15: 'L' (0x4C) for left ch, 'R' (0x52) for right, ' ' for mono
//   0x10-0x11: type marker 0x44 0x00 (LE u16 = 0x0044)
//   0x12-0x1B: metadata (zeros — sample rate stored in patch/partial params)
//   0x1C-0x1D: cum_off (u16 LE) — offset from audio base in 8192-byte units
//   0x1E-0x1F: length (u16 LE) — sample length in 8192-byte units
//
// Audio: 16-bit LE signed PCM, mono. The S-770 stores audio as LE despite
// being 68k-based — confirmed by envelope analysis in roland-parser.cjs.
//
// Format reference: RE'd from northstar-drumscapes.roland.iso + S770225.bin.
// Parser: roland-parser.cjs parseS7Disk() / extractS7Sample().

// ---- Disk geometry ---------------------------------------------------------
export const S7_DISK_SIZE    = 1474560;   // 1.44 MB 3.5" HD floppy
export const S7_BLOCK_SIZE   = 512;
export const S7_TOTAL_BLOCKS = S7_DISK_SIZE / S7_BLOCK_SIZE; // 2880
export const S7_AUDIO_UNIT  = 8192;       // PCH directory offset/length unit
export const S7_MAX_SAMPLES = 512;        // PCH directory capacity
export const S7_MAX_PATCHES = 16;

// ---- Disk offsets ----------------------------------------------------------
export const S7_OFFSETS = {
   FAT_START:    0x0400,
   PATCH_DIR:    0x1C00,
   PCH_DIR:      0x3C00,
   AUDIO_START:  0x6000,   // Aligned to 8192-byte boundary (3 × 8192 = 0x6000)
};

// FAT reserved marker
const S7_FAT_RESERVED = 0x23CF;
const S7_FAT_FREE     = 0x0000;
const S7_FAT_END      = 0xFFFF;

// ---- Volume tags -----------------------------------------------------------
const S7_VOLUME_TAGS = {
   'S-770': 'S770 MR25A',
   'S-760': 'S760 MR25A',
   'S-750': 'S750 MR25A',
};

// ---- Sample rates ----------------------------------------------------------
export const S7_SAMPLE_RATES = [
   { rate: 44100, label: '44100 Hz (CD quality, ~16s total)' },
   { rate: 22050, label: '22050 Hz (half rate, ~32s total)' },
   { rate: 11025, label: '11025 Hz (low rate, ~65s total)' },
];

// Audio capacity (bytes available for sample data)
export const S7_AUDIO_CAPACITY = S7_DISK_SIZE - S7_OFFSETS.AUDIO_START; // 1,450,560 bytes

/**
 * Calculate the number of 8192-byte audio units needed for a given byte count.
 */
function audioUnitsForBytes(byteLen) {
   if (byteLen <= 0) return 0;
   return Math.ceil(byteLen / S7_AUDIO_UNIT);
}

/**
 * Calculate the total audio data size for a set of samples (in 8192-byte units).
 *
 * @param {Array} samples - Array of { pcm: Int16Array }
 * @returns {number} - total audio units
 */
export function calcS7AudioUnits(samples) {
   let totalUnits = 0;
   for (const smp of samples) {
      if (!smp.pcm || smp.pcm.length === 0) continue;
      const pcmBytes = smp.pcm.length * 2;
      totalUnits += audioUnitsForBytes(pcmBytes);
   }
   return totalUnits;
}

/**
 * Check if samples fit on the disk.
 *
 * @param {Array} samples - Array of { pcm: Int16Array }
 * @returns {{ totalUnits: number, fits: boolean, maxUnits: number }}
 */
export function calcS7Layout(samples) {
   const totalUnits = calcS7AudioUnits(samples);
   const maxUnits = Math.floor(S7_AUDIO_CAPACITY / S7_AUDIO_UNIT); // ~177
   return { totalUnits, fits: totalUnits <= maxUnits, maxUnits };
}

/**
 * Write an ASCII string into a buffer at a given offset, space-padded.
 */
function writeName(buf, off, len, str) {
   const name = (str || '').substring(0, len).padEnd(len, ' ');
   for (let i = 0; i < len; i++) {
      buf[off + i] = name.charCodeAt(i) & 0x7F;
   }
}

/**
 * Build a PCH sample directory entry (32 bytes).
 *
 * @param {string} name - Sample name (will be formatted as XYZ:Name)
 * @param {number} cumOff - Cumulative offset in 8192-byte units from audio base
 * @param {number} length - Length in 8192-byte units
 * @param {string} channel - 'mono', 'L', or 'R'
 * @returns {Uint8Array} 32-byte entry
 */
function buildPchEntry(name, cumOff, length, channel) {
   const entry = new Uint8Array(32);
   const dv = new DataView(entry.buffer);

   // Format name as XYZ:Name.... (16 bytes, ':' at byte 3)
   // Use a generic 3-char prefix "SMP" if name doesn't already have XYZ: format
   let formatted;
   if (name.length >= 4 && name[3] === ':') {
      formatted = name.substring(0, 16);
   } else {
      // Truncate name to fit: "SMP:" + name (max 12 chars) = 16 bytes
      const baseName = (name || 'SAMPLE').substring(0, 12);
      formatted = 'SMP:' + baseName;
   }

   // Write 16-byte name, space-padded
   writeName(entry, 0, 16, formatted);

   // Byte 15: channel marker
   if (channel === 'L') entry[15] = 0x4C; // 'L'
   else if (channel === 'R') entry[15] = 0x52; // 'R'
   // else: mono, already space (0x20)

   // Bytes 16-17: type marker 0x44 0x00
   entry[16] = 0x44;
   entry[17] = 0x00;

   // Bytes 18-27: metadata (zeros = defaults)

   // Bytes 28-29: cum_off (u16 LE)
   dv.setUint16(0x1C, cumOff, true);

   // Bytes 30-31: length (u16 LE)
   dv.setUint16(0x1E, length, true);

   return entry;
}

/**
 * Build a patch directory entry (512 bytes).
 *
 * @param {string} name - Patch name (will be formatted as XYZ:Name*)
 * @returns {Uint8Array} 512-byte entry
 */
function buildPatchEntry(name) {
   const entry = new Uint8Array(512);

   // Format name as XYZ:Name* (15 bytes, '*' at end = byte 15)
   let formatted;
   if (name.length >= 4 && name[3] === ':') {
      formatted = name.substring(0, 15);
   } else {
      const baseName = (name || 'PATCH').substring(0, 11);
      formatted = 'SMP:' + baseName;
   }

   // Write name (up to 14 chars), then '*' at byte 15
   writeName(entry, 0, 15, formatted);
   entry[15] = 0x2A; // '*'

   // Bytes 16-511: patch parameters (zeros = defaults)
   return entry;
}

/**
 * Build a complete Roland S-770/S-760/S-750 floppy disk image.
 *
 * Each sample becomes a PCH directory entry. Samples are stored sequentially
 * as 16-bit LE signed PCM, aligned to 8192-byte units. A single patch is
 * created that references the first sample.
 *
 * @param {Array} samples - Array of { name, pcm: Int16Array, sampleRate }
 * @param {object} opts - { family: 'S-770'|'S-760'|'S-750', volumeName: string }
 * @returns {Uint8Array} 1,474,560-byte disk image
 */
export function buildS7Disk(samples, opts = {}) {
   const family = opts.family || 'S-770';
   const volumeName = (opts.volumeName || 'XTRC').substring(0, 16);

   const disk = new Uint8Array(S7_DISK_SIZE);
   const dv = new DataView(disk.buffer);

   // ---- Volume header (0x000 - 0x3FF) ---------------------------------------
   // Bytes 0-3: zeros (already zero)
   // Bytes 4-15: volume tag (12 ASCII)
   const tag = S7_VOLUME_TAGS[family] || S7_VOLUME_TAGS['S-770'];
   writeName(disk, 0x04, 12, tag);

   // Bytes 0x20-0x3F: system name (32 ASCII)
   writeName(disk, 0x20, 32, `${family} XTRC Volume`);

   // Bytes 0x100-0x10F: volume name (16 ASCII, space-padded)
   writeName(disk, 0x100, 16, volumeName);

   // Bytes 0x110-0x113: total blocks (u32 LE)
   dv.setUint32(0x110, S7_TOTAL_BLOCKS, true);

   // Bytes 0x114-0x11B: counts (u16 LE each)
   const validSamples = samples.filter(s => s.pcm && s.pcm.length > 0);
   dv.setUint16(0x114, 1, true);             // performances: 1
   dv.setUint16(0x116, 1, true);             // patches: 1
   dv.setUint16(0x118, validSamples.length, true); // partials
   dv.setUint16(0x11A, validSamples.length, true); // samples

   // ---- FAT (0x400 - 0x1BFF) ------------------------------------------------
   // Mark system blocks (0 to AUDIO_START/512-1) as reserved
   const audioStartBlock = S7_OFFSETS.AUDIO_START / S7_BLOCK_SIZE; // 48
   for (let i = 0; i < audioStartBlock; i++) {
      dv.setUint16(S7_OFFSETS.FAT_START + i * 2, S7_FAT_RESERVED, true);
   }

   // Chain audio blocks sequentially for each sample
   let blockIdx = audioStartBlock;
   for (const smp of validSamples) {
      const pcmBytes = smp.pcm.length * 2;
      const numBlocks = Math.ceil(pcmBytes / S7_BLOCK_SIZE);
      for (let b = 0; b < numBlocks; b++) {
         const fatOff = S7_OFFSETS.FAT_START + (blockIdx + b) * 2;
         if (blockIdx + b + 1 < S7_TOTAL_BLOCKS && b < numBlocks - 1) {
            dv.setUint16(fatOff, blockIdx + b + 1, true); // point to next
         } else {
            dv.setUint16(fatOff, S7_FAT_END, true); // end of chain
         }
      }
      blockIdx += numBlocks;
   }
   // Mark remaining blocks as free
   for (let i = blockIdx; i < S7_TOTAL_BLOCKS; i++) {
      dv.setUint16(S7_OFFSETS.FAT_START + i * 2, S7_FAT_FREE, true);
   }

   // ---- Patch directory (0x1C00) --------------------------------------------
   // One patch referencing the volume
   const patchEntry = buildPatchEntry(volumeName.substring(0, 11));
   disk.set(patchEntry, S7_OFFSETS.PATCH_DIR);

   // ---- PCH sample directory (0x3C00) ---------------------------------------
   // Write one entry per sample. Pad to at least 8 entries so the parser's
   // directory scan (which requires bestLen >= 8 consecutive valid entries)
   // can locate the PCH directory. Dummy entries have zero length and will
   // be silently skipped by the adapter during extraction.
   let cumOff = 0;
   const numEntries = Math.max(validSamples.length, 8);
   for (let i = 0; i < numEntries && i < S7_MAX_SAMPLES; i++) {
      if (i < validSamples.length) {
         const smp = validSamples[i];
         const pcmBytes = smp.pcm.length * 2;
         const numUnits = audioUnitsForBytes(pcmBytes);

         const smpName = (smp.name || `Sample ${i + 1}`).substring(0, 12);
         const pchEntry = buildPchEntry(smpName, cumOff, numUnits, 'mono');
         disk.set(pchEntry, S7_OFFSETS.PCH_DIR + i * 32);

         cumOff += numUnits;
      } else {
         // Padding entry: valid name + type marker, zero length
         const padName = `PAD${String(i).padStart(3, '0')}`;
         const pchEntry = buildPchEntry(padName, 0, 0, 'mono');
         disk.set(pchEntry, S7_OFFSETS.PCH_DIR + i * 32);
      }
   }

   // ---- Audio data (0x6000) -------------------------------------------------
   // Write 16-bit LE signed PCM for each sample, padded to 8192-byte boundary
   let audioOff = S7_OFFSETS.AUDIO_START;
   for (const smp of validSamples) {
      const pcm = smp.pcm;
      const pcmBytes = pcm.length * 2;
      const paddedBytes = audioUnitsForBytes(pcmBytes) * S7_AUDIO_UNIT;

      // Write PCM as 16-bit LE
      for (let j = 0; j < pcm.length; j++) {
         dv.setInt16(audioOff + j * 2, pcm[j], true);
      }
      // Remaining bytes in the padded unit are already zero (silence padding)

      audioOff += paddedBytes;
   }

   console.log(`[Roland S7] buildS7Disk: ${validSamples.length} samples, family=${family}, ` +
      `audio units=${cumOff}, disk=${S7_DISK_SIZE} bytes`);
   for (let i = 0; i < validSamples.length; i++) {
      const smp = validSamples[i];
      const pcmBytes = smp.pcm.length * 2;
      const units = audioUnitsForBytes(pcmBytes);
      console.log(`[Roland S7] sample ${i}: name="${smp.name}", pcm=${smp.pcm.length}, ` +
         `units=${units}, rate=${smp.sampleRate}`);
   }

   return disk;
}
